import * as React from 'react';
import classnames from 'classnames';
import './progress-block-bar.styles.less';

export class ProgressBlockBar extends React.Component<IProgressBlockBarProps> {
    private readonly PREFILLED_PART = 0.25;

    private isStepChecked = (step: IProgressBarStep): boolean => {
        return this.props.value >= step.count;
    }; 

    private getBarWidth = (): number => {
        const checkedStepsNumber = this.props.steps.reduce((total, step) => {
            return this.isStepChecked(step) 
                ? total + 1 
                : total;
        }, 0);

        const totalSteps = this.props.steps.length;
        const complete = checkedStepsNumber / totalSteps;

        const isPrefilled = checkedStepsNumber === 0 && !this.hasLeftTail();
        if (isPrefilled) {
            return (100 / totalSteps) * this.PREFILLED_PART;
        }
        
        return complete * 100;
    };

    private getSteps = (): JSX.Element[] => {
        return this.props.steps.map((step, index) => {
            return (
                <div 
                    className={classnames(
                        'sitegen-friend-referral-progress-bar__step', 
                        { 'sitegen-friend-referral-progress-bar__step--checked': this.isStepChecked(step) }
                    ) } 
                    key={ index } 
                >
                    <span className="sitegen-friend-referral-progress-bar__step__title">
                        { step.title }
                    </span>
                </div>
            );
        });
    };

    private hasLeftTail = (): boolean => {
        return !!this.props.leftTail && !this.props.vertical;
    };

    private getLeftTail = (): React.ReactNode => {
        if (!this.hasLeftTail()) {
            return null;
        }

        const filled = this.getBarWidth() > 0;
        return (
            <div className={classnames(
                'sitegen-friend-referral-progress-bar__left-tail',
                { 'sitegen-friend-referral-progress-bar__left-tail--filled': filled }
            )} />
        );
    };

    private getRightTail = (): React.ReactNode => {
        if (!this.props.rightTail || this.props.vertical) {
            return null;
        }

        const filled = this.getBarWidth() === 100;

        return (
            <div className={classnames(
                'sitegen-friend-referral-progress-bar__right-tail',
                { 'sitegen-friend-referral-progress-bar__right-tail--filled': filled }
            )} />
        );
    };

    private getBarProgress = (): React.ReactNode => {
        const parameter = this.props.vertical ? 'height' : 'width';
        return (
            <div 
                className="sitegen-friend-referral-progress-bar__progress"
                style={{ [parameter]: `${this.getBarWidth()}%` }}
            />
        );
    };

    public render() {
        const leftTail = this.getLeftTail();
        const rightTail = this.getRightTail();

        return (
            <div 
                className={classnames(
                    'sitegen-friend-referral-progress-bar',
                    { 'sitegen-friend-referral-progress-bar--vertical': this.props.vertical },
                )}
            >   
                <div className={classnames(
                    'sitegen-friend-referral-progress-bar__line-container',
                    {
                        'sitegen-friend-referral-progress-bar__line-container--left-padding': !!leftTail,
                        'sitegen-friend-referral-progress-bar__line-container--right-padding': !!rightTail,
                    }
                )}>
                    <div className="sitegen-friend-referral-progress-bar__line" />
                    { leftTail }
                    { rightTail }
                    { this.getBarProgress() }
                </div>

                <div className="sitegen-friend-referral-progress-bar__step-container">
                    { this.getSteps() }
                </div>
            </div>
        );
    }
}

export interface IProgressBarStep {
    count: number;
    title: string;
}

export interface IProgressBlockBarProps {
    readonly value: number;
    readonly steps: IProgressBarStep[];
    readonly vertical?: boolean;
    readonly leftTail?: boolean;
    readonly rightTail?: boolean;
}
